/*
 * Teacher Color Preference System
 * Allows users to mark teachers as Green (prioritize), Orange (second option), or Red (never take)
 */

// Global storage for teacher preferences
window.teacherPreferences = window.teacherPreferences || {};

/**
 * Set teacher color for a course
 */
export function setTeacherColor(courseCode, teacherName, color) {
    if (!window.teacherPreferences[courseCode]) {
        window.teacherPreferences[courseCode] = {
            green: [],
            orange: [],
            red: []
        };
    }
    
    const prefs = window.teacherPreferences[courseCode];
    
    // Remove teacher from all color arrays first
    prefs.green = prefs.green.filter(t => t !== teacherName);
    prefs.orange = prefs.orange.filter(t => t !== teacherName);
    prefs.red = prefs.red.filter(t => t !== teacherName);
    
    // Add to appropriate color array
    if (color === 'green') {
        prefs.green.push(teacherName);
    } else if (color === 'orange') {
        prefs.orange.push(teacherName);
    } else if (color === 'red') {
        prefs.red.push(teacherName);
    }
    
    // Save to localStorage
    saveTeacherPreferences();
    
    console.log(`Set ${teacherName} to ${color} for ${courseCode}`);
}

/**
 * Get teacher color for a course
 */
export function getTeacherColor(courseCode, teacherName) {
    if (!window.teacherPreferences[courseCode]) {
        return null;
    }
    
    const prefs = window.teacherPreferences[courseCode];
    
    if (prefs.green.includes(teacherName)) return 'green';
    if (prefs.orange.includes(teacherName)) return 'orange';
    if (prefs.red.includes(teacherName)) return 'red';
    
    return null;
}

/**
 * Remove teacher color
 */
export function removeTeacherColor(courseCode, teacherName) {
    if (!window.teacherPreferences[courseCode]) {
        return;
    }
    
    const prefs = window.teacherPreferences[courseCode];
    prefs.green = prefs.green.filter(t => t !== teacherName);
    prefs.orange = prefs.orange.filter(t => t !== teacherName);
    prefs.red = prefs.red.filter(t => t !== teacherName);
    
    saveTeacherPreferences();
}

/**
 * Save teacher preferences to localStorage
 */
function saveTeacherPreferences() {
    try {
        localStorage.setItem('teacherPreferences', JSON.stringify(window.teacherPreferences));
    } catch (error) {
        console.error('Error saving teacher preferences:', error);
    }
}

/**
 * Load teacher preferences from localStorage
 */
export function loadTeacherPreferences() {
    try {
        const saved = localStorage.getItem('teacherPreferences');
        if (saved) {
            window.teacherPreferences = JSON.parse(saved);
        }
    } catch (error) {
        console.error('Error loading teacher preferences:', error);
        window.teacherPreferences = {};
    }
}

/**
 * Add color selector to teacher elements
 */
export function addColorSelectorToTeacher(teacherElement, courseCode, teacherName) {
    // Don't add if already has selector
    if (teacherElement.querySelector('.teacher-color-badge')) {
        return;
    }
    
    // Get current color
    const currentColor = getTeacherColor(courseCode, teacherName);
    
    // Create color badge
    const badge = document.createElement('span');
    badge.className = 'teacher-color-badge';
    badge.style.cssText = 'cursor: pointer; margin-left: 5px; font-size: 0.8em;';
    
    updateBadgeDisplay(badge, currentColor);
    
    // Add click handler
    badge.addEventListener('click', (e) => {
        e.stopPropagation();
        showColorSelector(teacherElement, courseCode, teacherName, badge);
    });
    
    teacherElement.appendChild(badge);
}

/**
 * Update badge display based on color
 */
function updateBadgeDisplay(badge, color) {
    badge.innerHTML = '';
    
    if (color === 'green') {
        badge.innerHTML = '<i class="fas fa-circle" style="color: #28a745;"></i>';
        badge.title = 'Prioritize (Green)';
    } else if (color === 'orange') {
        badge.innerHTML = '<i class="fas fa-circle" style="color: #ffa500;"></i>';
        badge.title = 'Second Option (Orange)';
    } else if (color === 'red') {
        badge.innerHTML = '<i class="fas fa-circle" style="color: #dc3545;"></i>';
        badge.title = 'Never Take (Red)';
    } else {
        badge.innerHTML = '<i class="far fa-circle" style="color: #999;"></i>';
        badge.title = 'Click to set preference';
    }
}

/**
 * Show color selector dropdown
 */
function showColorSelector(teacherElement, courseCode, teacherName, badge) {
    // Remove any existing selector
    const existing = document.querySelector('.color-selector-dropdown');
    if (existing) {
        existing.remove();
    }
    
    // Create selector dropdown
    const dropdown = document.createElement('div');
    dropdown.className = 'color-selector-dropdown';
    dropdown.style.cssText = `
        position: absolute;
        background: white;
        border: 2px solid #ff69b4;
        border-radius: 8px;
        padding: 8px;
        box-shadow: 0 4px 15px rgba(255, 105, 180, 0.3);
        z-index: 1000;
        display: flex;
        gap: 8px;
    `;
    
    // Create color options
    const colors = [
        { name: 'green', icon: 'fas fa-check-circle', color: '#28a745', label: 'Prioritize' },
        { name: 'orange', icon: 'fas fa-minus-circle', color: '#ffa500', label: 'Second' },
        { name: 'red', icon: 'fas fa-times-circle', color: '#dc3545', label: 'Never' },
        { name: 'none', icon: 'far fa-circle', color: '#999', label: 'Clear' }
    ];
    
    colors.forEach(({name, icon, color, label}) => {
        const btn = document.createElement('button');
        btn.className = 'btn btn-sm';
        btn.style.cssText = `padding: 4px 8px; border: 1px solid ${color};`;
        btn.innerHTML = `<i class="${icon}" style="color: ${color};"></i><br><small>${label}</small>`;
        btn.title = label;
        
        btn.addEventListener('click', () => {
            if (name === 'none') {
                removeTeacherColor(courseCode, teacherName);
                updateBadgeDisplay(badge, null);
            } else {
                setTeacherColor(courseCode, teacherName, name);
                updateBadgeDisplay(badge, name);
            }
            dropdown.remove();
        });
        
        dropdown.appendChild(btn);
    });
    
    // Position dropdown
    const rect = teacherElement.getBoundingClientRect();
    dropdown.style.top = (rect.bottom + window.scrollY) + 'px';
    dropdown.style.left = rect.left + 'px';
    
    document.body.appendChild(dropdown);
    
    // Close on click outside
    setTimeout(() => {
        document.addEventListener('click', function closeDropdown(e) {
            if (!dropdown.contains(e.target)) {
                dropdown.remove();
                document.removeEventListener('click', closeDropdown);
            }
        });
    }, 0);
}

/**
 * Initialize teacher color system
 */
export function initializeTeacherColors() {
    loadTeacherPreferences();
    
    // Add color selectors to existing teachers
    // This will be called after course panel is loaded
    window.addEventListener('teachersLoaded', () => {
        addColorSelectorsToAllTeachers();
    });
    
    console.log('Teacher color system initialized');
}

/**
 * Add color selectors to all teacher elements
 */
function addColorSelectorsToAllTeachers() {
    // Find all teacher elements in the course panel
    const teacherElements = document.querySelectorAll('.dropdown-teacher li');
    
    teacherElements.forEach(li => {
        const teacherNameElement = li.querySelector('div:first-child');
        if (teacherNameElement) {
            const teacherName = teacherNameElement.textContent.trim();
            // Get course code from parent
            const courseElement = li.closest('.dropdown-teacher');
            const courseNameElement = courseElement?.querySelector('.cname');
            if (courseNameElement) {
                const fullText = courseNameElement.textContent.trim();
                const match = fullText.match(/^([A-Z]{3,4}\d{3,4}[A-Z]?)/);
                if (match) {
                    const courseCode = match[1];
                    addColorSelectorToTeacher(teacherNameElement, courseCode, teacherName);
                }
            }
        }
    });
}

// Export for global access
window.setTeacherColor = setTeacherColor;
window.getTeacherColor = getTeacherColor;
window.addColorSelectorsToAllTeachers = addColorSelectorsToAllTeachers;

export default initializeTeacherColors;

